/* ==========================================================================
   Fragrance Finder quiz — client engine for sections/quiz.liquid.
   A LEFT slide-out drawer (mirrors the theme's cart/search drawers): off-canvas
   until opened by a link/menu item to its #anchor (default #quiz) or any
   [data-quiz-open]. Inside: a one-screen-at-a-time state machine — path branch →
   per-flow questions → live fragrance search → loading → results. Several quizzes
   can co-exist (one per anchor); only one opens at a time.

   Results use weighted attribute scoring (computeResults): the answers' `match`
   values + per-question `weight` are scored against a LIVE product→attribute
   index (fetchMetaIndex, crawled from templates/collection.quiz-data.liquid — so
   it tracks the store's metafields with no static snapshot). The searched
   fragrance's dupe still anchors slot 0. "Why we picked this" is DEFERRED (needs
   AI copy — see quiz-flow doc), so it is not rendered. The result heart is the
   store's Swym wishlist hook.
   ========================================================================== */
(function () {
  'use strict';

  // Shopper-facing wording from the theme's language file (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.quiz;
    return (s && s[key]) || '';
  }

  var STORE_KEY = 'noted:quiz';
  var RESUME_MS = 24 * 60 * 60 * 1000; // resume a persisted session for 24h, then start clean
  // Sibling asset URL for quiz-npd.json (the upcoming-NPD pre-seed list,
  // generated by tag-standardization/scripts/10_sync_proper_name_metafields.py).
  // Derived from this script's own asset URL — captured now, at parse time.
  var NPD_URL = (function () {
    var src = (document.currentScript && document.currentScript.src) || '';
    return src ? src.replace(/quiz\.js(\?[^#]*)?$/, 'quiz-npd.json') : '';
  })();
  var QUIZZES = [];        // every controller on the page
  var byAnchor = {};       // anchor -> controller
  var openController = null;

  var HEART_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19.5 12.572 12 20l-7.5-7.428A5 5 0 1 1 12 6.006a5 5 0 1 1 7.5 6.566Z"/></svg>';

  // Whole amounts drop the decimals by design ("£30", not "£30.00"), so the
  // fraction digits vary per amount rather than being fixed at 2.
  // Format for the customer's MARKET, not the browser locale, so euro prices
  // read "24,95 €" (symbol-after) rather than the en-US "€24.95". See
  // assets/main-product.js marketLocale for the full rationale.
  var marketLocale = function (currency) {
    if (currency !== 'EUR') return undefined;
    var country = (window.Shopify && Shopify.country) || 'GB';
    return { NL: 'nl-NL', IE: 'en-IE', MT: 'en-IE', CY: 'en-IE' }[country] || 'de-DE';
  };
  var money = function (cents) {
    var major = (cents || 0) / 100;
    var frac = major % 1 === 0 ? 0 : 2;
    var currency = window.Shopify && Shopify.currency ? Shopify.currency.active : 'GBP';
    try {
      return new Intl.NumberFormat(marketLocale(currency), {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: frac
      }).format(major);
    } catch (e) {
      return '£' + major.toFixed(2).replace(/\.00$/, '');
    }
  };
  var stripInspired = function (vendor) {
    var v = (vendor || '').trim();
    return /^inspired by/i.test(v) ? v.slice(11).trim() : '';
  };

  function QuizController(root) {
    this.root = root;
    this.drawer = root.closest('[data-quiz-drawer]');
    this.anchor = this.drawer ? this.drawer.getAttribute('data-quiz-anchor') : null;
    this.lastTrigger = null;
    this.cfg = this.readConfig();
    this.screensEl = root.querySelector('.quiz__screens');
    this.screens = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-screen]'));
    this.nav = root.querySelector('[data-quiz-nav]');
    this.backBtn = root.querySelector('[data-quiz-back]');
    this.nextBtn = root.querySelector('[data-quiz-next]');
    this.progressFill = root.querySelector('[data-quiz-progress-fill]');
    this.progressBar = root.querySelector('[role="progressbar"]');
    this.counters = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-counter]'));

    this.state = { name: '', path: '', answers: {}, fragrances: [], skip: '', otherFragrance: '', gate: '' };
    this.sequence = [];
    this.index = 0;
    this.loadingTimer = null;

    this.byRole = {};
    for (var i = 0; i < this.screens.length; i++) {
      var s = this.screens[i];
      var role = s.getAttribute('data-screen-type');
      (this.byRole[role] = this.byRole[role] || []).push(s);
    }

    this.bind();
    this.start();

    QUIZZES.push(this);
    if (this.anchor) byAnchor[this.anchor] = this;
  }

  QuizController.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-quiz-config]');
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  };

  QuizController.prototype.bind = function () {
    var self = this;
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(); });
    if (this.backBtn) this.backBtn.addEventListener('click', function () { self.back(); });

    // Tile / row / quote / path / fragrance / skip selection (event delegation)
    this.root.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-quiz-option]');
      if (opt && self.root.contains(opt)) { self.selectOption(opt, true); return; }
      var skip = e.target.closest('[data-quiz-skip]');
      if (skip && self.root.contains(skip)) { self.selectSkip(skip); return; }
      var frag = e.target.closest('[data-quiz-frag]');
      if (frag && self.root.contains(frag)) { self.selectFragrance(frag); return; }
      var fragOther = e.target.closest('[data-quiz-frag-other-cta]');
      if (fragOther && self.root.contains(fragOther)) { self.adoptSearchAsOther(fragOther); return; }
      var gateGo = e.target.closest('[data-quiz-gate-submit]');
      if (gateGo && self.root.contains(gateGo)) { self.submitGate(); return; }
      var gateSkip = e.target.closest('[data-quiz-gate-skip]');
      if (gateSkip && self.root.contains(gateSkip)) { self.finishGate('skipped'); return; }
      var restart = e.target.closest('[data-quiz-restart]');
      if (restart && self.root.contains(restart)) { e.preventDefault(); self.restart(); return; }
    });

    // Name inputs (opening + loading share the state)
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-name-input]'), function (input) {
      input.addEventListener('input', function () {
        self.state.name = input.value.trim();
        // mirror across all name inputs
        Array.prototype.forEach.call(self.root.querySelectorAll('[data-quiz-name-input]'), function (o) {
          if (o !== input) o.value = input.value;
        });
        self.refreshNav();
      });
    });

    // Email gate: Enter in the email field submits
    var gateEmail = this.root.querySelector('[data-quiz-gate-email]');
    if (gateEmail) {
      gateEmail.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self.submitGate(); }
      });
    }

    // Live fragrance filter
    var searchInputs = this.root.querySelectorAll('[data-quiz-search-input]');
    Array.prototype.forEach.call(searchInputs, function (input) {
      input.addEventListener('input', function () { self.filterFragrances(input); });
    });

    // "Something else" capture (#7) — stored for brand-gap / NPD data only.
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-input]'), function (input) {
      input.addEventListener('input', function () {
        self.state.otherFragrance = input.value.trim();
        var screen = input.closest('[data-quiz-screen]');
        if (screen) self.syncFragListVisibility(screen); // emptied → the list comes back
        self.persist();
      });
    });

    // Arrow-key navigation for single-select radiogroups (ARIA radio model)
    this.root.addEventListener('keydown', function (e) {
      var opt = e.target.closest && e.target.closest('[data-quiz-option][role="radio"]');
      if (!opt || !self.root.contains(opt)) return;
      var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      e.preventDefault();
      var screen = opt.closest('[data-quiz-screen]');
      var opts = Array.prototype.slice.call(screen.querySelectorAll('[data-quiz-option][role="radio"]'));
      var idx = opts.indexOf(opt), nextOpt;
      if (e.key === 'Home') nextOpt = opts[0];
      else if (e.key === 'End') nextOpt = opts[opts.length - 1];
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') nextOpt = opts[(idx + 1) % opts.length];
      else nextOpt = opts[(idx - 1 + opts.length) % opts.length];
      if (nextOpt) { nextOpt.focus(); self.selectOption(nextOpt, false); }
    });
  };

  /* ---- Drawer open / close ---------------------------------------------- */
  QuizController.prototype.onOpen = function () {
    // (re)render current screen and move focus into the dialog
    this.show();
    // Warm the scoring index in the background while the user answers, so the
    // results screen is instant (no-op if this quiz has no results screen).
    if (this.byRole.results) this.fetchMetaIndex();
    var closeBtn = this.root.querySelector('[data-quiz-close]');
    try { (closeBtn || this.root).focus({ preventScroll: true }); } catch (e) {}
  };

  QuizController.prototype.onClose = function () {
    this.cancelAdvance();
    if (this.loadingTimer) { clearTimeout(this.loadingTimer); this.loadingTimer = null; }
  };

  QuizController.prototype.restart = function () {
    this.cancelAdvance();
    if (this.loadingTimer) { clearTimeout(this.loadingTimer); this.loadingTimer = null; }
    // Full clean slate — name AND gate included. "Start again" is an explicit
    // fresh run, often for a different person (partner/gift), so the gate asks
    // again (a retaker can use the skip link) and the name prompt re-appears.
    // Mid-run resume (reload within 24 h) still keeps a passed gate passed.
    this.state = { name: '', path: '', answers: {}, fragrances: [], skip: '', otherFragrance: '', gate: '', results: [] };
    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-quiz-option],[data-quiz-skip],[data-quiz-frag]'),
      function (o) { o.setAttribute('aria-checked', 'false'); }
    );
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-name-input]'), function (o) { o.value = ''; });
    // clear + collapse any "Something else" capture fields
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-input]'), function (o) { o.value = ''; });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-field]'), function (o) { o.hidden = true; });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-toggle]'), function (o) { o.setAttribute('aria-expanded', 'false'); });
    this.persist();
    this.start();
  };

  QuizController.prototype.start = function () {
    var self = this;
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) {}

    // 24h freshness cap — a session left longer than this starts clean (and the
    // stale record is cleared), so a week-old quiz never silently reopens mid-flow.
    var fresh = saved && typeof saved.savedAt === 'number' && (Date.now() - saved.savedAt) < RESUME_MS;
    if (saved && !fresh) { try { localStorage.removeItem(STORE_KEY); } catch (e) {} saved = null; }

    if (saved && saved.name && !this.state.name) this.state.name = saved.name;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-name-input]'), function (o) {
      if (self.state.name) o.value = self.state.name;
    });

    // Base sequence before a path is chosen: [name-opening?], path
    var base = [];
    if (this.cfg.namePosition === 'opening' && this.byRole.name) base.push(this.byRole.name[0]);
    if (this.byRole.path) base.push(this.byRole.path[0]);
    // Fallback: no path block authored → just run every question in DOM order
    if (!this.byRole.path) {
      this.sequence = this.screens.filter(function (s) {
        return ['name', 'question', 'search', 'loading', 'gate', 'results'].indexOf(s.getAttribute('data-screen-type')) > -1;
      });
    } else {
      this.sequence = base;
    }
    this.index = 0;

    // Positional restore: a fresh in-progress/completed session with a chosen path
    // resumes on the exact screen it was left — any screen — with Back/Start again
    // intact. Survives a full page reload (the in-memory controller already keeps
    // state across a same-page close/reopen). restoreSession() renders on success.
    if (fresh && saved && saved.path && this.byRole.path && this.restoreSession(saved)) return;

    this.show();
  };

  // Rehydrate a persisted session and land on its saved position.
  QuizController.prototype.restoreSession = function (saved) {
    this.state.path = saved.path || '';
    this.state.answers = saved.answers || {};
    this.state.fragrances = saved.fragrances || [];
    this.state.otherFragrance = saved.otherFragrance || '';
    this.state.skip = saved.skip || '';
    this.state.gate = saved.gate || '';
    this.state.results = saved.results || [];

    this.rebuildSequence(); // builds the path's screen list (uses state.path)

    var idx = parseInt(saved.index, 10);
    if (isNaN(idx) || idx < 0) idx = 0;
    if (idx > this.sequence.length - 1) idx = this.sequence.length - 1;
    this.index = idx;

    // Don't resume ON the loading interstitial — skip to results if we landed there.
    if (this.current() && this.current().getAttribute('data-screen-type') === 'loading' && this.byRole.results) {
      var ri = this.sequence.indexOf(this.byRole.results[0]);
      if (ri > -1) this.index = ri;
    }

    this.restoreSelections();
    this.show();
    return true;
  };

  // Reflect the restored answers back onto the DOM so going Back shows the right
  // tiles highlighted (state alone drives Next-gating, but not the visual state).
  QuizController.prototype.restoreSelections = function () {
    var self = this;
    // path option
    if (this.state.path && this.byRole.path) {
      Array.prototype.forEach.call(this.byRole.path[0].querySelectorAll('[data-quiz-option]'), function (o) {
        o.setAttribute('aria-checked', String(o.getAttribute('data-path-value') === self.state.path));
      });
    }
    // question answers → option tiles (match screen by id, no selector escaping)
    Object.keys(this.state.answers).forEach(function (sid) {
      var ans = self.state.answers[sid];
      if (!ans || !ans.values) return;
      var screen = null;
      for (var i = 0; i < self.screens.length; i++) {
        if (self.screens[i].getAttribute('data-screen-id') === sid) { screen = self.screens[i]; break; }
      }
      if (!screen) return;
      Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-option]'), function (o) {
        o.setAttribute('aria-checked', String(ans.values.indexOf(o.getAttribute('data-value')) > -1));
      });
    });
    // fragrance-search skip + "Something else" capture field
    if (this.state.skip) {
      Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-skip]'), function (o) {
        if (o.getAttribute('data-skip') === self.state.skip) o.setAttribute('aria-checked', 'true');
      });
      if (this.state.skip === 'other' && this.state.otherFragrance) {
        Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-input]'), function (input) {
          input.value = self.state.otherFragrance;
        });
        Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-other-toggle]'), function (t) {
          var screen = t.closest('[data-quiz-screen]');
          if (screen) self.syncOtherField(screen); // reveals the field per the toggle's checked state
        });
      }
    }
    // fragrance tile selections are re-applied by mountFragrances when that screen shows
  };

  QuizController.prototype.rebuildSequence = function () {
    var path = this.state.path;
    var seq = [];
    if (this.cfg.namePosition === 'opening' && this.byRole.name) seq.push(this.byRole.name[0]);
    if (this.byRole.path) seq.push(this.byRole.path[0]);
    this.screens.forEach(function (s) {
      var type = s.getAttribute('data-screen-type');
      if (type !== 'question' && type !== 'search') return;
      var flow = (s.getAttribute('data-flow') || 'all').trim();
      if (flow === 'all' || flow === '' || flow.split(/\s+/).indexOf(path) > -1) seq.push(s);
    });
    if (this.byRole.loading) seq.push(this.byRole.loading[0]);
    // Email gate sits before results; once passed (submitted OR skipped) it drops
    // out of the sequence entirely, so Back from results skips over it too.
    if (this.byRole.gate && !this.state.gate) seq.push(this.byRole.gate[0]);
    if (this.byRole.results) seq.push(this.byRole.results[0]);
    // Preserve current position by screen identity
    var current = this.sequence[this.index];
    this.sequence = seq;
    var newIdx = seq.indexOf(current);
    this.index = newIdx > -1 ? newIdx : this.index;
  };

  QuizController.prototype.current = function () { return this.sequence[this.index]; };

  QuizController.prototype.show = function () {
    this.cancelAdvance();
    var active = this.current();
    if (!active) return;
    this.screens.forEach(function (s) { s.classList.toggle('is-active', s === active); });
    var type = active.getAttribute('data-screen-type');

    if (type === 'search') this.mountFragrances(active);
    if (type === 'loading') this.runLoading();
    if (type === 'results') this.renderResults();

    this.updateProgress();
    this.refreshNav();
    this.setupRoving(active);

    // Analytics only. Every screen change passes through here, so this is the one
    // place that can report quiz engagement and completion without assets/tracking.js
    // having to reach into this state machine. Nothing here reads the answers.
    try {
      document.dispatchEvent(new CustomEvent('quiz:screen', {
        detail: {
          screen: type || '',
          step: this.index + 1,
          total: this.sequence.length,
          quiz: this.anchor || ''
        }
      }));
    } catch (e) { /* never let analytics break the quiz */ }

    // scroll the panel back to top + move focus into the new screen (a11y — and
    // the loading→results auto-advance would otherwise drop focus to <body>).
    if (this.screensEl) this.screensEl.scrollTop = 0;
    active.scrollTop = 0;
    if (!active.hasAttribute('tabindex')) active.setAttribute('tabindex', '-1');
    try { active.focus({ preventScroll: true }); } catch (e) {}
  };

  QuizController.prototype.setupRoving = function (screen) {
    var radios = screen.querySelectorAll('[data-quiz-option][role="radio"]');
    if (!radios.length) return;
    var checkedOne = screen.querySelector('[data-quiz-option][role="radio"][aria-checked="true"]');
    Array.prototype.forEach.call(radios, function (r) { r.setAttribute('tabindex', '-1'); });
    (checkedOne || radios[0]).setAttribute('tabindex', '0');
  };

  QuizController.prototype.updateProgress = function () {
    // The path branch is not a numbered question — only questions + search count.
    var steps = this.sequence.filter(function (s) {
      var t = s.getAttribute('data-screen-type');
      return t === 'question' || t === 'search';
    });
    var active = this.current();
    var type = active.getAttribute('data-screen-type');
    var pos = steps.indexOf(active);
    var total = steps.length || 1;
    var pct;
    if (pos > -1) pct = ((pos + 1) / total) * 100;
    else if (type === 'loading' || type === 'gate' || type === 'results') pct = 100;
    else pct = 0; // name / path (pre-flow)
    if (this.progressFill) this.progressFill.style.width = pct + '%';
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(Math.round(pct)));
    var label = pos > -1 ? ('Q ' + (pos + 1) + ' of ' + total) : '';
    this.counters.forEach(function (c) {
      if (active.contains(c)) c.textContent = label;
    });
  };

  QuizController.prototype.getType = function () { return this.current().getAttribute('data-screen-type'); };

  QuizController.prototype.refreshNav = function () {
    var type = this.getType();
    var showBack = this.index > 0;
    // Next is hidden on the results screen (own CTAs), the email gate (its submit
    // and skip controls own the advance), and on auto-advance screens.
    var showNext = type !== 'results' && type !== 'gate' && !this.screenAutoAdvances();
    // Hide the whole bar when it would be empty (e.g. the opening path step).
    var showNav = type !== 'results' && (showBack || showNext);
    if (this.nav) {
      this.nav.style.display = showNav ? 'flex' : 'none';
      this.nav.setAttribute('data-single', showBack ? 'false' : 'true');
      // Back is the lone control on auto-advance screens → let it fill the row.
      this.nav.setAttribute('data-back-only', (showBack && !showNext) ? 'true' : 'false');
    }
    if (this.backBtn) this.backBtn.style.visibility = showBack ? 'visible' : 'hidden';
    if (this.nextBtn) {
      this.nextBtn.style.display = showNext ? '' : 'none';
      if (showNext) {
        // Gate NEXT when the screen isn't satisfied. aria-disabled (not native
        // `disabled`) so it stays focusable/announced; next() no-ops when unsatisfied.
        var ok = this.isSatisfied();
        this.nextBtn.setAttribute('aria-disabled', String(!ok));
        this.nextBtn.classList.toggle('is-disabled', !ok);
      }
    }
  };

  QuizController.prototype.isSatisfied = function () {
    var s = this.current();
    var type = this.getType();
    if (type === 'name') {
      return s.getAttribute('data-required') !== 'true' || this.state.name.length > 0;
    }
    if (type === 'path') return !!this.state.path;
    if (type === 'question') {
      if (s.getAttribute('data-required') === 'false') return true;
      var ans = this.state.answers[s.getAttribute('data-screen-id')];
      return !!(ans && ans.values && ans.values.length);
    }
    if (type === 'search') return true; // optional: empty = "open to anything"
    if (type === 'loading') return true;
    if (type === 'gate') return true; // advance happens via submit/skip, not Next
    return true;
  };

  QuizController.prototype.next = function () {
    if (!this.isSatisfied()) return;
    var type = this.getType();
    if (type === 'path') this.rebuildSequence();
    if (this.loadingTimer) { clearTimeout(this.loadingTimer); this.loadingTimer = null; }
    if (this.index < this.sequence.length - 1) { this.index++; this.persist(); this.show(); }
  };

  QuizController.prototype.back = function () {
    this.cancelAdvance();
    if (this.loadingTimer) { clearTimeout(this.loadingTimer); this.loadingTimer = null; }
    if (this.index > 0) { this.index--; this.show(); }
  };

  /* ---- Auto-advance (client feedback #6) --------------------------------- *
   * On a committing selection (click / Enter / Space — NOT arrow browsing),
   * single-select question screens and the path step move on by themselves
   * after a short beat (so the green check is seen first). Multi-select,
   * fragrance search and name capture keep the Next button. */
  QuizController.prototype.screenAutoAdvances = function (screen) {
    screen = screen || this.current();
    if (!screen) return false;
    var type = screen.getAttribute('data-screen-type');
    if (type === 'path') return true;
    if (type === 'question') {
      var multi = screen.getAttribute('data-multi') === 'true';
      var optional = screen.getAttribute('data-required') === 'false';
      return !multi && !optional;
    }
    return false;
  };

  QuizController.prototype.cancelAdvance = function () {
    if (this.advanceTimer) { clearTimeout(this.advanceTimer); this.advanceTimer = null; }
  };

  QuizController.prototype.queueAdvance = function (screen) {
    if (!this.screenAutoAdvances(screen)) return;
    var self = this;
    this.cancelAdvance();
    this.advanceTimer = setTimeout(function () {
      self.advanceTimer = null;
      // guard: still on the same, now-satisfied screen (user may have gone back)
      if (self.current() === screen && self.isSatisfied()) self.next();
    }, 240);
  };

  /* ---- Selection --------------------------------------------------------- */
  QuizController.prototype.selectOption = function (opt, commit) {
    var screen = opt.closest('[data-quiz-screen]');
    var type = screen.getAttribute('data-screen-type');
    var multi = screen.getAttribute('data-multi') === 'true';
    var group = screen.querySelectorAll('[data-quiz-option]');

    if (type === 'path') {
      Array.prototype.forEach.call(group, function (o) { o.setAttribute('aria-checked', 'false'); });
      opt.setAttribute('aria-checked', 'true');
      this.state.path = opt.getAttribute('data-path-value');
      this.refreshNav();
      if (commit) this.queueAdvance(screen);
      return;
    }

    var checked = opt.getAttribute('aria-checked') === 'true';
    if (!multi) {
      // Single-select = radio semantics: a re-tap must NOT deselect (that would
      // empty a required answer and silently block Next). Force the tapped one on.
      Array.prototype.forEach.call(group, function (o) {
        o.setAttribute('aria-checked', 'false');
        o.setAttribute('tabindex', '-1');
      });
      opt.setAttribute('aria-checked', 'true');
      opt.setAttribute('tabindex', '0');
    } else {
      opt.setAttribute('aria-checked', String(!checked));
    }
    this.captureAnswer(screen);
    this.refreshNav();
    if (commit) this.queueAdvance(screen);
  };

  QuizController.prototype.captureAnswer = function (screen) {
    var sid = screen.getAttribute('data-screen-id');
    var chosen = screen.querySelectorAll('[data-quiz-option][aria-checked="true"]');
    var values = [], labels = [], products = [];
    Array.prototype.forEach.call(chosen, function (o) {
      if (o.getAttribute('data-value')) values.push(o.getAttribute('data-value'));
      if (o.getAttribute('data-label')) labels.push(o.getAttribute('data-label'));
      var ph = o.getAttribute('data-products');
      if (ph) products = products.concat(ph.split(',').filter(Boolean));
    });
    this.state.answers[sid] = {
      values: values, labels: labels, products: products,
      weight: parseFloat(screen.getAttribute('data-weight') || '1') || 1,
      question: screen.getAttribute('data-question') || ''
    };
  };

  QuizController.prototype.selectSkip = function (skip) {
    var screen = skip.closest('[data-quiz-screen]');
    var was = skip.getAttribute('aria-checked') === 'true';
    var isOther = skip.hasAttribute('data-quiz-other-toggle');
    Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-skip]'), function (o) { o.setAttribute('aria-checked', 'false'); });
    // "I wear something else" can coexist with picked fragrances (they wear
    // several, one we don't stock); the true skips ("I don't know") cannot.
    if (!isOther) {
      Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-frag]'), function (o) { o.setAttribute('aria-checked', 'false'); });
      this.state.fragrances = [];
    }
    skip.setAttribute('aria-checked', String(!was));
    this.state.skip = was ? '' : (skip.getAttribute('data-skip') || 'skip');
    // "Something else" (#7): reveal a capture-only text field when picked, hide it
    // (and drop what was typed) when deselected or another skip is chosen.
    this.syncOtherField(screen);
    this.refreshNav();
  };

  // Show/hide the "Something else" free-text field to mirror its toggle's state.
  // The text is captured for brand-gap / NPD data only — it never feeds the matcher.
  QuizController.prototype.syncOtherField = function (screen) {
    var self = this;
    Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-other-toggle]'), function (toggle) {
      var on = toggle.getAttribute('aria-checked') === 'true';
      toggle.setAttribute('aria-expanded', String(on));
      var id = toggle.getAttribute('aria-controls');
      var field = id && screen.querySelector('#' + id);
      if (!field) return;
      field.hidden = !on;
      var input = field.querySelector('[data-quiz-other-input]');
      if (on) {
        if (input) { try { input.focus(); } catch (e) {} }
      } else if (input) {
        input.value = '';
        self.state.otherFragrance = '';
      }
    });
    this.syncFragListVisibility(screen);
  };

  // Focus mode for "I wear something else" (Tim's Loom, 22 Jul): while the
  // capture field holds text, the search box + A–Z list hide — the screen shows
  // just the skips + what they typed. Deleting the text back to empty re-shows
  // the list. Picked fragrances stay selected underneath throughout.
  QuizController.prototype.syncFragListVisibility = function (screen) {
    var toggle = screen.querySelector('[data-quiz-other-toggle]');
    var on = toggle && toggle.getAttribute('aria-checked') === 'true';
    var input = screen.querySelector('[data-quiz-other-input]');
    var focus = on && !!(input && input.value.trim());
    var search = screen.querySelector('.quiz__search');
    var mount = screen.querySelector('[data-quiz-frag-mount]');
    if (search) search.hidden = focus;
    if (mount) mount.hidden = focus;
  };

  QuizController.prototype.selectFragrance = function (frag) {
    var screen = frag.closest('[data-quiz-screen]');
    // choosing a fragrance clears the true skips ("I don't know") but keeps
    // "I wear something else" + its typed text — the two can coexist.
    Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-skip]:not([data-quiz-other-toggle])'), function (o) { o.setAttribute('aria-checked', 'false'); });
    if (this.state.skip !== 'other') this.state.skip = '';
    this.syncOtherField(screen);
    var was = frag.getAttribute('aria-checked') === 'true';
    frag.setAttribute('aria-checked', String(!was));
    var entry = {
      name: frag.getAttribute('data-name'),
      handle: frag.getAttribute('data-handle'),
      productId: frag.getAttribute('data-product-id'),
      npd: frag.hasAttribute('data-npd') || undefined
    };
    this.state.fragrances = this.state.fragrances.filter(function (f) { return f.handle !== entry.handle; });
    if (!was) this.state.fragrances.push(entry);
    this.refreshNav();
  };

  /* ---- Fragrance search -------------------------------------------------- */
  // Live A–Z list of "Inspired By …" fragrances, built from /products.json
  // (Liquid can't loop >50 products/request, so we fetch client-side once),
  // joined with the metafield index for custom.proper_name ("Brand, Fragrance",
  // synced from Athina's sheet). Entries sharing a proper name merge into ONE
  // search entry (the three Aventus vendor spellings → "Creed Aventus"), the
  // proper name becomes the display name, and each entry keeps its Noted product
  // title for the second display line + "reverse" search matching. Degrades to
  // the old vendor-derived list when the index is unavailable (pn just absent).
  QuizController.prototype.fetchFragrances = function () {
    var self = this;
    if (this._frags) return Promise.resolve(this._frags);
    try {
      var cached = JSON.parse(sessionStorage.getItem('noted:quiz:frags3') || 'null');
      if (cached && cached.length) { this._frags = cached; return Promise.resolve(cached); }
    } catch (e) {}

    // "Rome - Shower Gel" / "Rome Body Lotion" → "Rome" for display + matching
    var cleanTitle = function (t) {
      return (t || '').split(' - ')[0]
        .replace(/\s*(shower gel|body lotion|100ml diffuser|diffuser|wax melt|candle)\s*$/i, '')
        .trim();
    };

    var acc = [];
    function getPage(n) {
      return fetch('/products.json?limit=250&page=' + n)
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return d.products || []; });
    }
    function loop(n) {
      return getPage(n).then(function (ps) {
        acc = acc.concat(ps);
        return (ps.length === 250 && n < 6) ? loop(n + 1) : acc;
      });
    }
    // Upcoming-NPD pre-seed (assets/quiz-npd.json): fragrances Athina has named
    // that aren't live yet still appear in the search so we capture the demand.
    // Each entry is suppressed the moment a live product covers it (by proper
    // name or Noted title), so the list can never duplicate the catalogue.
    var npdFetch = NPD_URL
      ? fetch(NPD_URL).then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { return (d && d.pending) || []; })
          .catch(function () { return []; })
      : Promise.resolve([]);

    return Promise.all([loop(1), this.fetchMetaIndex(), npdFetch]).then(function (res) {
      var all = res[0];
      var pnByHandle = {};
      (res[1] || []).forEach(function (i) { if (i.pn) pnByHandle[i.handle] = i.pn; });
      var map = {};
      var liveTitles = {};
      all.forEach(function (p) {
        var inspired = stripInspired(p.vendor);
        if (!inspired) return;
        var pn = pnByHandle[p.handle] || '';
        var display = (pn || inspired).replace(', ', ' '); // "Chanel, No. 5" → "Chanel No. 5"
        var key = display.toLowerCase();
        var title = p.title || '';
        var clean = title.indexOf(' - ') === -1 && !/shower gel|diffuser|lotion|wax melt|candle|body /i.test(title);
        var g = map[key];
        if (!g) g = map[key] = { name: display, sub: '', handle: p.handle, id: p.id, clean: false, tokens: {} };
        // search tokens: proper name (with + without comma), every Inspired-by
        // vendor variant, every cleaned Noted title ("reverse logic")
        [pn, display, inspired, cleanTitle(title)].forEach(function (t) {
          if (t) g.tokens[t.toLowerCase()] = 1;
        });
        if (clean && !g.clean) {
          g.handle = p.handle; g.id = p.id; g.clean = true; g.sub = cleanTitle(title);
        } else if (!g.sub) {
          g.sub = cleanTitle(title);
        }
        liveTitles[cleanTitle(title).toLowerCase()] = 1;
      });
      (res[2] || []).forEach(function (e) {
        if (!e || !e.pn) return;
        var display = e.pn.replace(', ', ' ');
        var key = display.toLowerCase();
        if (map[key]) return;                                  // live product already carries this proper name
        if (e.title && liveTitles[e.title.toLowerCase()]) return; // live product already owns this Noted title
        var g = map[key] = { name: display, sub: e.title || '', handle: 'npd-' + key.replace(/[^a-z0-9]+/g, '-'), id: '', npd: true, tokens: {} };
        [e.pn, display, e.title].forEach(function (t) { if (t) g.tokens[t.toLowerCase()] = 1; });
      });
      var list = Object.keys(map).map(function (k) {
        var g = map[k];
        return { name: g.name, sub: g.sub, handle: g.handle, id: g.id, npd: !!g.npd, search: Object.keys(g.tokens).join('|') };
      }).sort(function (a, b) { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); });
      self._frags = list;
      try { sessionStorage.setItem('noted:quiz:frags3', JSON.stringify(list)); } catch (e) {}
      return list;
    });
  };

  // Live product→attribute index for weighted scoring, crawled in the background
  // from the Liquid endpoint (templates/collection.quiz-data.liquid) so the data
  // is always current with the store's metafields — no static snapshot. Prefetched
  // on drawer open (idle time while the user answers); sessionStorage-cached.
  // Always resolves (never rejects) — [] on failure, so results degrade cleanly.
  QuizController.prototype.fetchMetaIndex = function () {
    var self = this;
    if (this._metaIndex) return Promise.resolve(this._metaIndex);
    if (this._metaIndexPromise) return this._metaIndexPromise;
    try {
      var cached = JSON.parse(sessionStorage.getItem('noted:quiz:meta2') || 'null');
      if (cached && cached.length) { this._metaIndex = cached; return Promise.resolve(cached); }
    } catch (e) {}

    var base = (this.cfg.results && this.cfg.results.indexUrl) || '/collections/all?view=quiz-data';
    var acc = [];
    function getPage(n) {
      var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'page=' + n;
      // NB: no 'Accept: application/json' header — it makes Shopify serve its
      // native /collections/all JSON API and ignore the ?view= template. The
      // template returns a JSON body regardless; r.json() parses by content.
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : { products: [], next: false }; })
        .catch(function () { return { products: [], next: false }; });
    }
    function loop(n) {
      return getPage(n).then(function (d) {
        acc = acc.concat(d.products || []);
        return (d.next && n < 8) ? loop(n + 1) : acc;
      });
    }
    this._metaIndexPromise = loop(1).then(function (all) {
      self._metaIndex = all;
      try { sessionStorage.setItem('noted:quiz:meta2', JSON.stringify(all)); } catch (e) {}
      return all;
    }).catch(function () { return []; });
    return this._metaIndexPromise;
  };

  QuizController.prototype.mountFragrances = function (screen) {
    var mount = screen.querySelector('[data-quiz-frag-mount]');
    if (!mount || mount.getAttribute('data-mounted') === 'true') return;
    mount.setAttribute('data-mounted', 'true');
    mount.innerHTML = '<div class="quiz__spinner" role="status" aria-label="' + esc(str('loading_fragrances')) + '"></div>';
    var self = this;
    this.fetchFragrances().then(function (list) {
      var grid = document.createElement('div');
      grid.className = 'quiz__frag-grid';
      grid.setAttribute('data-quiz-frag-grid', '');
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', str('fragrances_label'));
      list.forEach(function (f) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'quiz__frag';
        b.setAttribute('data-quiz-frag', '');
        b.setAttribute('role', 'checkbox'); // valid host for aria-checked
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('data-name', f.name.toLowerCase());
        b.setAttribute('data-search', f.search || f.name.toLowerCase());
        b.setAttribute('data-handle', f.handle);
        b.setAttribute('data-product-id', f.id);
        if (f.npd) b.setAttribute('data-npd', '');
        // Designer name primary; the Noted product title as a lighter second
        // line so it's clearly OUR fragrance, not the brand (21 July call).
        // Upcoming-NPD entries say so — selectable (demand capture) but the
        // taker isn't led to expect the dupe in their results yet.
        var sub = f.npd
          ? (f.sub ? str('noted_coming_soon').replace('{name}', f.sub) : str('coming_soon'))
          : (f.sub ? str('noted_name').replace('{name}', f.sub) : '');
        b.innerHTML = '<span class="quiz__frag-name">' + esc(f.name) + '</span>'
          + (sub ? '<span class="quiz__frag-sub">' + esc(sub) + '</span>' : '');
        grid.appendChild(b);
      });
      var otherToggle = screen.querySelector('[data-quiz-other-toggle]');
      var empty = document.createElement('p');
      empty.className = 'quiz__frag-empty';
      empty.setAttribute('data-quiz-frag-empty', '');
      empty.setAttribute('role', 'status'); // announce the no-match hint
      empty.hidden = true;
      empty.textContent = otherToggle
        ? str('no_match')
        : str('no_match_pick');
      grid.appendChild(empty);
      // No-match → free-text conversion: what they typed becomes the
      // "I wear something else" submission in one tap (2026-07-21 call).
      if (otherToggle) {
        var cta = document.createElement('button');
        cta.type = 'button';
        cta.className = 'quiz__frag-other-cta';
        cta.setAttribute('data-quiz-frag-other-cta', '');
        cta.hidden = true;
        grid.appendChild(cta);
      }
      mount.innerHTML = '';
      mount.appendChild(grid);
      // re-apply any prior multi-select choices (back/forward nav)
      self.state.fragrances.forEach(function (sel) {
        var el = grid.querySelector('[data-handle="' + sel.handle + '"]');
        if (el) el.setAttribute('aria-checked', 'true');
      });
      // restored session with "something else" text → stay in focus mode
      self.syncFragListVisibility(screen);
    }).catch(function () {
      mount.innerHTML = '<p class="quiz__frag-empty">' + esc(str('load_error')) + '</p>';
    });
  };

  QuizController.prototype.filterFragrances = function (input) {
    var screen = input.closest('[data-quiz-screen]');
    var q = input.value.trim().toLowerCase();
    var cards = screen.querySelectorAll('[data-quiz-frag]');
    var shown = 0;
    Array.prototype.forEach.call(cards, function (c) {
      var name = c.getAttribute('data-search') || c.getAttribute('data-name') || '';
      var hit = !q || name.indexOf(q) > -1;
      c.hidden = !hit;
      if (hit) shown++;
    });
    var noMatch = shown === 0 && !!q;
    var empty = screen.querySelector('[data-quiz-frag-empty]');
    if (empty) empty.hidden = !noMatch;
    var cta = screen.querySelector('[data-quiz-frag-other-cta]');
    var toggle = screen.querySelector('[data-quiz-other-toggle]');
    if (cta) {
      cta.hidden = !noMatch;
      if (noMatch) {
        var otherLabel = toggle ? toggle.textContent.trim() : str('something_else');
        cta.innerHTML = esc(str('add_other'))
          .replace('{term}', '<strong>' + esc(input.value.trim()) + '</strong>')
          .replace('{label}', esc(otherLabel));
      }
      // The CTA IS "I wear something else" for this moment — showing the skip
      // option too is a duplicate (Tim's Loom, 22 Jul). Back when matches return.
      if (toggle) toggle.hidden = noMatch;
    }
  };

  // No-match CTA: adopt the search text as the "I wear something else" entry —
  // selects the other option (keeping any picked fragrances), pastes the typed
  // text down into the capture field, and resets the search so the list returns.
  QuizController.prototype.adoptSearchAsOther = function (cta) {
    var screen = cta.closest('[data-quiz-screen]');
    var toggle = screen && screen.querySelector('[data-quiz-other-toggle]');
    if (!toggle) return;
    var searchInput = screen.querySelector('[data-quiz-search-input]');
    var typed = searchInput ? searchInput.value.trim() : '';
    Array.prototype.forEach.call(screen.querySelectorAll('[data-quiz-skip]'), function (o) { o.setAttribute('aria-checked', 'false'); });
    toggle.setAttribute('aria-checked', 'true');
    this.state.skip = 'other';
    this.syncOtherField(screen);
    var otherInput = screen.querySelector('[data-quiz-other-input]');
    if (otherInput && typed) {
      var cur = otherInput.value.trim();
      otherInput.value = cur && cur.toLowerCase().indexOf(typed.toLowerCase()) === -1 ? cur + ', ' + typed : (cur || typed);
      this.state.otherFragrance = otherInput.value.trim();
    }
    if (searchInput) { searchInput.value = ''; this.filterFragrances(searchInput); }
    this.syncFragListVisibility(screen); // field now has text → search + list hide
    this.persist();
    this.refreshNav();
  };

  /* ---- Loading ----------------------------------------------------------- */
  QuizController.prototype.runLoading = function () {
    var self = this;
    var dur = parseInt(this.cfg.loadingDuration, 10) || 2600;
    if (this.loadingTimer) clearTimeout(this.loadingTimer);
    this.loadingTimer = setTimeout(function () {
      self.loadingTimer = null;
      if (self.getType() === 'loading') self.next();
    }, dur);
  };

  /* ---- Email gate (data capture before results) ---------------------------
     POST the quiz payload to the capture endpoint (cfg.gateEndpoint), which
     upserts the customer + quiz tags via the Admin API (works for existing
     customers and non-consenting takers — see quiz-capture/README.md).
     On failure: one retry (first attempts can die to a cold start), then the
     payload is stashed in localStorage and re-sent silently on a later page
     load. No native-form fallback — a scripted {% form 'customer' %} POST is
     rejected by Shopify's spam protection ("Missing CAPTCHA token"), landing
     the taker on an error page mid-quiz. Failure never blocks the results. */

  QuizController.prototype.buildCapture = function () {
    var self = this;
    var activeIds = {};
    this.sequence.forEach(function (s) { activeIds[s.getAttribute('data-screen-id')] = true; });
    var answers = {};
    function push(k, v) {
      k = String(k || '').trim(); v = String(v || '').trim();
      if (!k || !v) return;
      (answers[k] = answers[k] || []).push(v);
    }
    Object.keys(this.state.answers).forEach(function (sid) {
      if (!activeIds[sid]) return; // abandoned-path answers must not leak in
      var a = self.state.answers[sid];
      (a.values || []).forEach(function (v, idx) {
        var i = String(v).indexOf(':');
        // concept values are "axis:key" (gender:feminine) — group by axis.
        // "none" means the answer doesn't feed matching — capture the answer
        // LABEL under the question instead, so the tag keeps what they picked
        // (quiz:what-brings-you-here-today:i-usually-buy-designer-…, not :none).
        // Anything else groups under the question text as-is.
        if (i > 0) push(v.slice(0, i), v.slice(i + 1));
        else if (String(v).toLowerCase() === 'none') push(a.question || sid, (a.labels || [])[idx] || '');
        else push(a.question || sid, v);
      });
    });
    this.state.fragrances.forEach(function (f) { push('fragrance', f.name); });
    if (this.state.path) push('path', this.state.path);
    if (this.state.skip) {
      // Tag the ticked option's wording, not its internal code — the same code
      // carries different labels per flow ("I don't know — I'm open to anything"
      // on self vs "They wear fragrance but I'm not sure what" on gift).
      var skipBtn = this.root.querySelector('[data-quiz-skip][aria-checked="true"]');
      var skipLabel = skipBtn ? (skipBtn.textContent || '').trim() : '';
      push('usual-fragrance', skipLabel || this.state.skip);
    }
    return { answers: answers, other: this.state.otherFragrance || '' };
  };

  var PENDING_KEY = 'noted:quiz-capture-pending';

  function postCapture(endpoint, payload) {
    var signal;
    try {
      var ctrl = new AbortController();
      setTimeout(function () { ctrl.abort(); }, 6000);
      signal = ctrl.signal;
    } catch (e) {}
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: signal
    }).then(function (r) { if (!r.ok) throw new Error('capture ' + r.status); });
  }

  function stashPendingCapture(endpoint, payload) {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ endpoint: endpoint, payload: payload, t: Date.now() }));
    } catch (e) {}
  }

  // Re-send a stashed capture on a later page load; drop it once it lands
  // (or once it's older than the quiz resume window).
  function flushPendingCapture() {
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch (e) {}
    if (!saved || !saved.endpoint || !saved.payload) return;
    if (Date.now() - (saved.t || 0) > RESUME_MS) {
      try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
      return;
    }
    postCapture(saved.endpoint, saved.payload)
      .then(function () { try { localStorage.removeItem(PENDING_KEY); } catch (e) {} })
      .catch(function () {}); // still pending — retried on the next load
  }

  QuizController.prototype.submitGate = function () {
    var self = this;
    var screen = this.byRole.gate && this.byRole.gate[0];
    if (!screen) return;
    var emailIn = screen.querySelector('[data-quiz-gate-email]');
    var email = (emailIn && emailIn.value || '').trim();
    var consentBox = screen.querySelector('[data-quiz-gate-consent]');
    var consent = !!(consentBox && consentBox.checked);
    var hp = (screen.querySelector('[data-quiz-gate-hp]') || {}).value || '';
    var err = screen.querySelector('[data-quiz-gate-error]');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (err) { err.textContent = str('invalid_email'); err.hidden = false; }
      if (emailIn) { try { emailIn.focus(); } catch (e) {} }
      return;
    }
    if (err) err.hidden = true;

    var btn = screen.querySelector('[data-quiz-gate-submit]');
    if (btn) {
      if (btn.getAttribute('aria-disabled') === 'true') return; // double-submit guard
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
    }

    var capture = this.buildCapture();
    var payload = { email: email, consent: consent, answers: capture.answers, other: capture.other, hp: hp };
    var done = function () { self.finishGate('submitted'); };

    var endpoint = (this.cfg.gateEndpoint || '').trim();
    if (!endpoint) { done(); return; }

    var send = function () {
      postCapture(endpoint, payload)
        .then(done)
        .catch(function () {
          postCapture(endpoint, payload) // one retry — the first hit warms the function
            .then(done)
            .catch(function () {
              stashPendingCapture(endpoint, payload);
              done(); // never block the results
            });
        });
    };

    // Attach the products the results screen is about to recommend (same
    // scoring, same index — prefetched on drawer open, and fetchMetaIndex
    // always resolves). Any hiccup just sends the capture without them.
    this.fetchMetaIndex().then(function (index) {
      try { payload.recommended = self.computeResults(index || []); } catch (e) {}
      send();
    }, send);
  };

  QuizController.prototype.finishGate = function (mode) {
    var screen = this.byRole.gate && this.byRole.gate[0];
    var btn = screen && screen.querySelector('[data-quiz-gate-submit]');
    if (btn) { btn.setAttribute('aria-disabled', 'false'); btn.classList.remove('is-disabled'); }
    this.state.gate = mode;
    this.rebuildSequence(); // gate drops out; results takes its slot
    this.persist();
    this.show();
  };

  /* ---- Results (weighted attribute scoring) ------------------------------
     Scores the live metafield index (fetchMetaIndex) against the concept values
     + per-question weights captured from the chosen answers, then assembles:
       slot 0  best match  — the searched-fragrance dupe (strongest signal),
                             or the top scorer when no fragrance was picked
       slot 1  alternative — next best scorer
       slot 2  wildcard    — best scorer from a scent family not already used
     Answer-pinned products then the merchant fallback set backfill empty slots.
     With no index (fetch failed / not ready) this degrades to anchor + pinned +
     fallback — i.e. the old stub behaviour, never a blank result. -------------- */
  QuizController.prototype.computeResults = function (index) {
    var self = this;
    // active-flow screen ids — answers from an abandoned path must not leak in
    var activeIds = {};
    this.sequence.forEach(function (s) { activeIds[s.getAttribute('data-screen-id')] = true; });

    // Gather chosen concept values (grouped by axis, carrying the strongest
    // question weight that selected them) + any answer-pinned products.
    var wanted = { gender: {}, scent: {}, occasion: {}, category: {} };
    var pinned = [];
    Object.keys(this.state.answers).forEach(function (sid) {
      if (!activeIds[sid]) return;
      var a = self.state.answers[sid], w = a.weight || 1;
      (a.products || []).forEach(function (h) { if (h) pinned.push(h); });
      (a.values || []).forEach(function (v) {
        var i = String(v).indexOf(':');
        if (i < 0) return;
        var axis = v.slice(0, i), key = v.slice(i + 1);
        if (!key || !wanted[axis]) return;
        wanted[axis][key] = Math.max(wanted[axis][key] || 0, w);
      });
    });

    // Score the live index. Gender is a HARD filter (chosen gender or unisex /
    // ungendered stay eligible); scent + occasion are weighted additive; a chosen
    // category adds a soft boost.
    var byHandle = {}, ranked = [];
    var genders = Object.keys(wanted.gender);
    (index || []).forEach(function (p) {
      byHandle[p.handle] = p;
      if (p.nf) return; // exclude non-fragrances (shower gel / diffuser / …) — #10
      if (genders.length) {
        var g = p.g || '';
        if (g && g !== 'unisex' && genders.indexOf(g) < 0) return;
      }
      var score = 0, hits = 0;
      ['scent', 'occasion'].forEach(function (axis) {
        var set = p[axis] || [];
        Object.keys(wanted[axis]).forEach(function (key) {
          if (set.indexOf(key) > -1) { score += wanted[axis][key]; hits++; }
        });
      });
      var cats = p.c || [];
      Object.keys(wanted.category).forEach(function (key) {
        if (cats.indexOf(key) > -1) score += wanted.category[key] || 1;
      });
      if (score > 0) ranked.push({ h: p.handle, score: score, hits: hits, fam: (p.scent || [])[0] || '' });
    });
    ranked.sort(function (a, b) { return (b.score - a.score) || (b.hits - a.hits); });

    var famOf = function (h) { var p = byHandle[h]; return p ? (p.scent || [])[0] || '' : ''; };
    var chosen = [], used = {};
    var take = function (h) { if (h && !used[h]) { used[h] = 1; chosen.push(h); return true; } return false; };

    // slot 0 — searched-fragrance dupe, else top scorer. Upcoming-NPD picks
    // (npd-* pseudo-handles) have no product to anchor — skip to the first
    // real selection; capture still records the NPD demand.
    var anchorSel = this.state.fragrances.filter(function (f) {
      return f.handle && String(f.handle).indexOf('npd-') !== 0;
    })[0];
    if (anchorSel) take(anchorSel.handle);
    var ri = 0;
    while (chosen.length < 1 && ri < ranked.length) take(ranked[ri++].h);
    // slot 1 — next best scorer
    while (chosen.length < 2 && ri < ranked.length) take(ranked[ri++].h);
    // slot 2 — wildcard: best remaining scorer from an unused scent family
    var usedFams = {};
    chosen.forEach(function (h) { usedFams[famOf(h)] = 1; });
    for (var i = 0; i < ranked.length && chosen.length < 3; i++) {
      var r = ranked[i];
      if (used[r.h] || (r.fam && usedFams[r.fam])) continue;
      take(r.h);
    }
    // no distinct family available → fill with the next best scorer
    for (var j = 0; j < ranked.length && chosen.length < 3; j++) take(ranked[j].h);

    // backfill empty slots: answer-pinned products, then merchant fallback
    pinned.forEach(take);
    (this.cfg.results && this.cfg.results.fallback || []).forEach(take);
    return chosen.slice(0, 3);
  };

  QuizController.prototype.renderResults = function () {
    var self = this;
    var screen = this.current();
    var listEl = screen.querySelector('[data-quiz-results-list]');
    var headEl = screen.querySelector('[data-quiz-results-heading]');
    var introEl = screen.querySelector('[data-quiz-results-intro]');
    var rc = this.cfg.results || {};

    if (headEl) {
      var tmpl = rc.heading || str('results_heading');
      var text;
      if (this.state.name) {
        text = tmpl.replace('{name}', this.state.name);
      } else {
        // drop the "{name}, " lead-in entirely and re-capitalise
        text = tmpl.replace(/\{name\}[,\s]*/i, '');
        text = text.charAt(0).toUpperCase() + text.slice(1);
      }
      headEl.textContent = text;
    }
    if (introEl) introEl.textContent = this.buildIntro();

    if (!listEl) return;
    listEl.innerHTML = '<div class="quiz__spinner" role="status" aria-label="' + esc(str('loading_results')) + '"></div>';
    // Ensure the scoring index (usually already warm from onOpen), then score.
    this.fetchMetaIndex().then(function (index) {
      var handles = self.computeResults(index);
      if (!handles.length) { listEl.innerHTML = '<p class="quiz__results-intro">' + esc(str('no_fallback')) + '</p>'; return; }

      return Promise.all(handles.map(function (h) {
        return fetch('/products/' + h + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
      })).then(function (raw) {
      // Voice-swap the labels so {their}/{pronoun} follow the flow (e.g. the
      // wildcard reads "your wildcard" on self/browse, "their wildcard" on gift — #19).
      var badges = (rc.badges || [str('badge_best'), str('badge_alt'), str('badge_wild')])
        .map(function (b) { return self.applyVoice(b); });
      var products = [];
      listEl.innerHTML = '';
      // Badge is bound to the PRE-filter index so a failed middle fetch doesn't
      // relabel the wildcard as the alternative.
      raw.forEach(function (p, i) {
        if (!p) return;
        products.push(p);
        listEl.appendChild(self.resultCard(p, badges[i] || '', rc, i));
      });
      if (!products.length) {
        listEl.innerHTML = '<p class="quiz__results-intro">' + esc(str('results_error')) + '</p>';
        return;
      }
      self.state.results = products.map(function (p) { return p.handle; });
      self.persist();
      });
    });
  };

  QuizController.prototype.buildIntro = function () {
    var rc = this.cfg.results || {};
    // v1: clean, curated intro copy. Answer-aware personalisation is the deferred
    // AI-copy phase. {pronoun}/{their} tokens swap to the gift voice on the gift
    // flow (client feedback #9): "…we think you (or they) will love!".
    var tmpl = rc.introFallback || str('results_intro');
    return this.applyVoice(tmpl);
  };

  // Swap pronoun tokens to match the active flow: gift → they/their, else you/your.
  QuizController.prototype.applyVoice = function (str) {
    var gift = this.state.path === 'gift';
    return String(str == null ? '' : str)
      .replace(/\{pronoun\}/gi, gift ? 'they' : 'you')
      .replace(/\{their\}/gi, gift ? 'their' : 'your');
  };

  QuizController.prototype.resultCard = function (product, badge, rc, slot) {
    var wrap = document.createElement('div');
    wrap.className = 'quiz__result';
    var badgeMod = ['quiz__badge--best', 'quiz__badge--alt', 'quiz__badge--wild'][slot] || '';

    var avail = (product.variants || []).filter(function (v) { return v.available; });
    var variant = avail[0] || (product.variants || [])[0] || {};
    // prefer an AVAILABLE 5ml for the "From" price; never show/post a sold-out 5ml
    var isFive = function (v) { return /(^|\s)5\s*ml/i.test(v.title || '') || /5\s*ml/i.test(v.public_title || ''); };
    var fivevar = avail.filter(isFive)[0];
    var showFive = !!fivevar;
    var priceVar = fivevar || variant;
    // prefer the proper designer name (custom.proper_name via the meta index —
    // loaded by the time results render) over the raw Inspired-by vendor
    var pn = '';
    (this._metaIndex || []).some(function (i) {
      if (i.handle === product.handle && i.pn) { pn = i.pn.replace(', ', ' '); return true; }
      return false;
    });
    var inspired = pn || stripInspired(product.vendor);
    var img = product.featured_image || (product.images && product.images[0]) || '';

    // Wishlist heart = the store's Swym hook (same class product-card uses) —
    // assets/swym-wishlist.js paints + binds it by data-product-handle, even
    // though these render into the drawer long after page load.
    var heart = img
      ? '<button type="button" class="quiz__result-heart swym-add-to-wishlist" aria-label="' + esc(str('add_to_wishlist').replace('{title}', product.title)) + '" aria-pressed="false" data-product-id="' + esc(product.id) + '" data-product-handle="' + esc(product.handle) + '">' + HEART_SVG + '</button>'
      : '';

    wrap.innerHTML =
      (badge ? '<div class="quiz__badge ' + badgeMod + '">' + esc(badge) + '</div>' : '') +
      '<div class="quiz__result-card">' +
        '<div class="quiz__result-media">' +
          (img ? '<img src="' + esc(img) + '&width=560" alt="' + esc(product.title) + '" loading="lazy">' : '') +
          heart +
        '</div>' +
        '<div class="quiz__result-body">' +
          '<div class="quiz__result-name">' + esc(product.title) + '</div>' +
          (inspired ? '<div class="quiz__result-inspired">' + esc(str('inspired_by').replace('{name}', inspired)) + '</div>' : '') +
          '<div class="quiz__result-price"><span class="from">' + esc(str('from')) + ' </span><span class="amt">' + money(priceVar.price || product.price) + '</span>' +
            (showFive ? ' <span>(5ml)</span>' : '') + '</div>' +
        '</div>' +
      '</div>';

    // ── Quick-add: choose a size (#15) + add in the background (#14) ──────────
    // Clicking the CTA reveals the available sizes + a link to the PDP; picking a
    // size adds it silently (basket + header update, quiz stays open) and the
    // button confirms "Added <size>". Never offers/posts a sold-out size.
    var actions = document.createElement('div');
    actions.className = 'quiz__result-actions';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'btn btn--lg btn--outlined btn--full quiz__result-cta';
    trigger.setAttribute('data-quiz-add-trigger', '');
    trigger.innerHTML = '<span class="quiz__result-cta-spin" aria-hidden="true"></span><span data-cta-label>' + esc(rc.addLabel || str('add_to_basket')) + '</span>';
    var url = product.url || ('/products/' + product.handle);

    if (!avail.length) {
      trigger.disabled = true;
      trigger.querySelector('[data-cta-label]').textContent = str('sold_out');
      actions.appendChild(trigger);
      wrap.appendChild(actions);
      return wrap;
    }

    // One available size → add straight away, no picker (mirrors product-card).
    if (avail.length === 1) {
      var only = avail[0];
      trigger.addEventListener('click', function () { addToCart(only.id, trigger, sizeLabel(only), rc); });
      actions.appendChild(trigger);
      wrap.appendChild(actions);
      return wrap;
    }

    var picker = document.createElement('div');
    picker.className = 'quiz__result-picker';
    picker.setAttribute('data-quiz-picker', '');
    picker.hidden = true;

    var sizes = document.createElement('div');
    sizes.className = 'quiz__result-sizes';
    sizes.setAttribute('role', 'group');
    sizes.setAttribute('aria-label', str('choose_size_for').replace('{title}', product.title));
    avail.forEach(function (v) {
      var s = document.createElement('button');
      s.type = 'button';
      s.className = 'quiz__size';
      s.setAttribute('data-quiz-size', '');
      s.innerHTML = '<span class="quiz__size-ml">' + esc(sizeLabel(v)) + '</span>' +
                    '<span class="quiz__size-price">' + money(v.price) + '</span>';
      s.addEventListener('click', function () {
        addToCart(v.id, trigger, sizeLabel(v), rc);
        picker.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      });
      sizes.appendChild(s);
    });

    var more = document.createElement('a');
    more.className = 'quiz__result-more';
    more.href = url;
    more.textContent = rc.moreLabel || str('view_details');

    picker.appendChild(sizes);
    picker.appendChild(more);

    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'quiz-picker-' + slot);
    picker.id = 'quiz-picker-' + slot;
    trigger.addEventListener('click', function () {
      var open = !picker.hidden;
      picker.hidden = open;
      trigger.setAttribute('aria-expanded', String(!open));
    });

    actions.appendChild(trigger);
    actions.appendChild(picker);
    wrap.appendChild(actions);
    return wrap;
  };

  // Size label from a variant — the single "Size" option value (e.g. "5ml").
  function sizeLabel(v) {
    var t = (v && (v.option1 || v.title) || '').trim();
    return t && t.toLowerCase() !== 'default title' ? t : '';
  }

  QuizController.prototype.persist = function () {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        name: this.state.name, path: this.state.path,
        answers: this.state.answers, fragrances: this.state.fragrances,
        otherFragrance: this.state.otherFragrance || '',
        skip: this.state.skip || '',
        gate: this.state.gate || '',
        index: this.index,
        results: this.state.results || [],
        savedAt: Date.now()
      }));
    } catch (e) {}
  };

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  // Background add-to-basket from the results screen (client feedback #14): the
  // quiz STAYS OPEN and the cart drawer never opens. We add with the bundled
  // cart-drawer section, then hand the fresh HTML to the cart layer to swap
  // SILENTLY (window.NotedCart.renderSilently → header bubble + drawer stay in
  // sync, drawer stays shut). The button is the only feedback: spinner → "Added".
  function addToCart(variantId, btn, sizeText, rc) {
    if (!variantId || !btn || btn.dataset.busy === 'true') return;
    var label = btn.querySelector('[data-cta-label]');
    btn.dataset.busy = 'true';
    btn.disabled = true;
    btn.classList.add('is-adding');
    btn.classList.remove('is-added');
    if (label) label.textContent = str('adding');

    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    var cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer && cartDrawer.dataset.sectionId) {
      body.sections = cartDrawer.dataset.sectionId;
      body.sections_url = window.location.pathname;
    }
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok, data: d }; });
    }).then(function (res) {
      if (!res.ok) throw res.data;
      // Silent basket + header update; quiz keeps focus. Fallback (cart layer not
      // present) opens the drawer the old way rather than losing the add.
      if (window.NotedCart && window.NotedCart.renderSilently) {
        window.NotedCart.renderSilently(res.data.sections);
      } else {
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { sections: res.data.sections } }));
      }
      btn.classList.remove('is-adding');
      btn.classList.add('is-added');
      btn.disabled = false;
      btn.dataset.busy = 'false';
      var added = (rc && rc.addedLabel) || str('added');
      if (label) label.textContent = sizeText ? (added + ' ' + sizeText) : added;
    }).catch(function (err) {
      btn.classList.remove('is-adding');
      btn.disabled = false;
      btn.dataset.busy = 'false';
      if (label) label.textContent = (rc && rc.addLabel) || str('add_to_basket');
      document.dispatchEvent(new CustomEvent('cart:add-error', {
        detail: { message: (err && (err.description || err.message)) || '' }
      }));
    });
  }

  /* ---- Drawer layer (open/close/anchor dispatch — shared across quizzes) - */
  function pageWrap(el) {
    var n = el;
    while (n && n.parentElement !== document.body) n = n.parentElement;
    return n || el;
  }

  function setSiblingsInert(drawer, on) {
    var wrap = pageWrap(drawer);
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === wrap) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') return;
      if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    });
  }

  function markTriggers(anchor, on) {
    if (!anchor) return;
    document.querySelectorAll('[data-quiz-open="' + anchor + '"], a[href="#' + anchor + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]),select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; }
    );
  }

  function openQuiz(ctrl, trigger) {
    if (!ctrl || openController === ctrl) return;
    if (openController) closeQuiz();
    // Let any other open drawer (esp. the header hamburger menu, from whose CTA
    // this quiz is usually opened) close itself BEFORE we inert the page below —
    // its own teardown toggles inert on the same body siblings, so if it closed
    // afterwards it would clobber our sibling-inert. Listeners no-op if closed.
    document.dispatchEvent(new CustomEvent('quiz:open', { detail: { anchor: ctrl.anchor } }));
    openController = ctrl;
    ctrl.lastTrigger = trigger || null;
    var d = ctrl.drawer;
    setSiblingsInert(d, true);
    d.hidden = false;
    void d.offsetWidth; // reflow so the slide runs from the closed state
    d.classList.add('is-open');
    document.body.classList.add('noted-drawer-open');
    markTriggers(ctrl.anchor, true);
    // Also decorate the actual opener (covers bare [data-quiz-open] + cross-page
    // href*="#anchor" triggers that markTriggers' exact-match selector misses).
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
    ctrl.onOpen();
  }

  function closeQuiz() {
    var ctrl = openController;
    if (!ctrl) return;
    var d = ctrl.drawer;
    d.classList.remove('is-open');
    document.body.classList.remove('noted-drawer-open');
    setSiblingsInert(d, false);
    markTriggers(ctrl.anchor, false);
    // clear the hash if it points at this quiz, so the same link reopens it
    if (ctrl.anchor && location.hash === '#' + ctrl.anchor) {
      history.replaceState(null, '', location.pathname + location.search);
    }
    var trig = ctrl.lastTrigger;
    ctrl.lastTrigger = null;
    if (trig && trig.setAttribute) trig.setAttribute('aria-expanded', 'false');
    openController = null;
    ctrl.onClose();
    setTimeout(function () { if (openController !== ctrl) d.hidden = true; }, 360);
    // Return focus to the opener; if opened via deep-link / hashchange (no trigger),
    // fall back to the main content region rather than dropping focus to <body>.
    if (trig && trig.focus) { try { trig.focus(); } catch (e) {} }
    else {
      var main = document.getElementById('main-content');
      if (main) { main.setAttribute('tabindex', '-1'); try { main.focus({ preventScroll: true }); } catch (e) {} }
    }
  }

  function anchorFromEl(el) {
    if (el.hasAttribute('data-quiz-open')) {
      var v = el.getAttribute('data-quiz-open');
      if (v) return v;
      // bare [data-quiz-open] opens the only/first quiz
      return QUIZZES.length ? QUIZZES[0].anchor : null;
    }
    var href = el.getAttribute('href') || '';
    var hash = href.indexOf('#') > -1 ? href.slice(href.indexOf('#') + 1) : '';
    return hash || null;
  }

  function wireDispatcher() {
    document.addEventListener('click', function (e) {
      // close (header ✕ or backdrop — both carry data-quiz-close)
      var closeBtn = e.target.closest('[data-quiz-close]');
      if (closeBtn && openController && openController.drawer.contains(closeBtn)) {
        e.preventDefault();
        closeQuiz();
        return;
      }
      // open trigger
      var opener = e.target.closest('[data-quiz-open], a[href*="#"]');
      if (!opener) return;
      var anchor = anchorFromEl(opener);
      if (anchor && byAnchor[anchor]) {
        e.preventDefault();
        openQuiz(byAnchor[anchor], opener);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!openController) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeQuiz(); return; }
      if (e.key === 'Tab') {
        // Scope the trap to the PANEL so the full-screen backdrop button (which
        // matches "button" but is tabindex=-1) is never the wrap target.
        var panel = openController.drawer.querySelector('.quiz-drawer__panel') || openController.drawer;
        var f = focusables(panel);
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!openController.drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
    });

    // Defensive: if any adder opens the cart while a quiz is open, relinquish the
    // quiz modal so the two drawers don't clobber each other's inert/scroll-lock.
    document.addEventListener('cart:adding', function () { if (openController) closeQuiz(); });

    window.addEventListener('hashchange', function () {
      var a = location.hash ? location.hash.slice(1) : '';
      if (a && byAnchor[a]) openQuiz(byAnchor[a], triggerFor(a));
    });
  }

  // Resolve a real in-page opener for triggerless (deep-link/hashchange) opens, so
  // aria-expanded + focus-restore have an element to work with.
  function triggerFor(anchor) {
    return document.querySelector('[data-quiz-open="' + anchor + '"], a[href="#' + anchor + '"]');
  }

  function init() {
    document.documentElement.classList.remove('no-js');
    Array.prototype.forEach.call(document.querySelectorAll('[data-quiz]'), function (root) {
      if (root.getAttribute('data-quiz-init') === 'true') return;
      root.setAttribute('data-quiz-init', 'true');
      new QuizController(root);
    });
    wireDispatcher();
    flushPendingCapture();
    // deep-link: open on load if the URL hash matches a quiz anchor
    var a = location.hash ? location.hash.slice(1) : '';
    if (a && byAnchor[a]) openQuiz(byAnchor[a], triggerFor(a));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
